import { DocumentIndexProgress } from "@/models/workspace/DocumentIndexStatus";

/**
 * A document with kildeklargøring in flight, as the files views show it
 * (LAR-486). "starting": accepted this session, first status read pending.
 * "finalising": the run is done and this session is completing it.
 */
export type DocumentIndexingView = {
  stage: "starting" | "running" | "finalising";
  progress: DocumentIndexProgress | null;
};

type PhaseCopy = {
  label: string;
  /** What the phase counts, for "120 af 375 afsnit"; absent when it counts nothing. */
  unit?: string;
};

/**
 * The worker's phase keys (services/python-services/document-indexing,
 * index_progress callers). A key missing here still renders, as the generic
 * running text, so a worker can add a phase before the web-app knows it.
 */
const PHASES: Record<string, PhaseCopy> = {
  reading: { label: "Læser filen" },
  converting_docx: { label: "Konverterer Word-dokumentet til PDF" },
  converting: { label: "Konverterer PDF", unit: "sider" },
  storing: { label: "Gemmer dokumentet", unit: "sider" },
  // The worker prepares one document at a time; the others wait (LAR-620).
  queued: { label: "I kø bag et andet dokument" },
  chunking: { label: "Opdeler i afsnit" },
  redacting: { label: "Fjerner personoplysninger", unit: "afsnit" },
  embedding: { label: "Opbygger søgeindeks", unit: "afsnit" },
};

const GENERIC: PhaseCopy = { label: "Kildeklargøring kører" };
const STARTING: PhaseCopy = { label: "Starter kildeklargøring" };
const FINALISING: PhaseCopy = { label: "Afslutter kildeklargøring" };

export const INDEXING_EXPECTATION =
  "Store dokumenter kan tage lang tid, især når flere dokumenter klargøres på én gang.";

export type IndexingProgressDescription = {
  /** "Fjerner personoplysninger · 120 af 375 afsnit · 9 min". */
  line: string;
  /** Tooltip heading: "Kildeklargøring kører · 9 min". */
  title: string;
  /** Tooltip detail: "Fjerner personoplysninger (120 af 375 afsnit)"; null before a phase is known. */
  detail: string | null;
  /** "har ikke svaret i 7 min" once the worker has stopped; null while it is alive. */
  stalled: string | null;
};

const MINUTE_MS = 60_000;

/** Whole minutes since an ISO timestamp, never negative; null when unparseable. */
const minutesSince = (iso: string, now: number): number | null => {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  return Math.floor(Math.max(0, now - at) / MINUTE_MS);
};

/** "under 1 min" or "N min" since an ISO timestamp. */
export const formatMinutesSince = (iso: string, now: number): string | null => {
  const minutes = minutesSince(iso, now);
  if (minutes === null) return null;
  return minutes < 1 ? "under 1 min" : `${minutes} min`;
};

const stalledText = (
  progress: DocumentIndexProgress,
  now: number
): string | null => {
  if (!progress.stalled) return null;
  // The API decides a run has stalled; a client clock running behind the
  // server's must not turn that into "has not answered for under a minute".
  const minutes = progress.heartbeatAt
    ? minutesSince(progress.heartbeatAt, now)
    : null;
  return minutes ? `har ikke svaret i ${minutes} min` : "har ikke svaret";
};

const counterText = (
  progress: DocumentIndexProgress,
  unit?: string
): string | null => {
  const { done, total } = progress;
  if (total === null || total <= 0) return null;
  if (done === null) return unit ? `${total} ${unit}` : null;
  return unit ? `${done} af ${total} ${unit}` : `${done} af ${total}`;
};

const copyFor = (view: DocumentIndexingView): PhaseCopy => {
  if (view.stage === "starting") return STARTING;
  if (view.stage === "finalising") return FINALISING;
  const phase = view.progress?.phase;
  return (phase && PHASES[phase]) || GENERIC;
};

/** The words for a document's kildeklargøring in flight, at time `now` (ms). */
export const describeIndexingProgress = (
  view: DocumentIndexingView,
  now: number
): IndexingProgressDescription => {
  const copy = copyFor(view);
  const progress = view.stage === "running" ? view.progress : null;
  const knownPhase = copy !== GENERIC && progress !== null;

  const counter =
    progress && knownPhase ? counterText(progress, copy.unit) : null;
  const elapsed = progress ? formatMinutesSince(progress.startedAt, now) : null;

  const line = [copy.label, counter, elapsed].filter(Boolean).join(" · ");
  const title = [view.stage === "running" ? GENERIC.label : copy.label, elapsed]
    .filter(Boolean)
    .join(" · ");
  const detail =
    view.stage === "running" && knownPhase
      ? counter
        ? `${copy.label} (${counter})`
        : copy.label
      : null;

  return {
    line,
    title,
    detail,
    stalled: progress ? stalledText(progress, now) : null,
  };
};
